import React, { useCallback, useEffect, useState } from 'react';

import {

  apiFetch,

  apiResponseError,

  getStoredAdminView,

  parseJsonResponse,

  setStoredAdminView,

  SE_BRAND,

  PRODUCT_ENTITLEMENT_OPTIONS,

  isSuperadminRole,

} from '../constants';

import OrgUsersPanel from './OrgUsersPanel';



const PLATFORM_TENANT_ID = 'social-engineer';



const emptyForm = {

  tenant_id: '',

  name: '',

  product_entitlement: 'wraith',

};



const REQUESTS_TAB_LABEL = 'Service or Additional Requests';



function requestTypeLabel(item) {

  if (item.request_type === 'client_support') {

    if (item.category === 'service') return 'Client service request';

    if (item.category === 'help') return 'Client help request';

    return 'Client request';

  }

  return item.wants_services ? 'Interested in services/training' : 'Questions only';

}



function isPlatformOrg(row) {

  return row.tenant_id === PLATFORM_TENANT_ID;

}



function canDeleteOrg(row, actorRole) {

  return isSuperadminRole(actorRole) && !isPlatformOrg(row);

}



export default function AdminPortal({ onBack, authProfile }) {

  const storedView = getStoredAdminView();

  const [tenants, setTenants] = useState([]);

  const [inquiries, setInquiries] = useState([]);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState(null);

  const [success, setSuccess] = useState(null);

  const [form, setForm] = useState(emptyForm);

  const [creating, setCreating] = useState(false);

  const [deletingOrgId, setDeletingOrgId] = useState(null);

  const [expandedOrgId, setExpandedOrgId] = useState(null);

  const [activeTab, setActiveTab] = useState(storedView.tab || 'organizations');



  const changeTab = (tab) => {

    setActiveTab(tab);

    setStoredAdminView({ open: true, tab });

  };



  const loadData = useCallback(async () => {

    setLoading(true);

    setError(null);

    try {

      const [tenantRes, inquiryRes] = await Promise.all([

        apiFetch('/api/admin/tenants'),

        apiFetch('/api/admin/inquiries'),

      ]);

      const tenantData = await parseJsonResponse(tenantRes);
      if (!tenantRes.ok) {
        throw new Error(apiResponseError(tenantRes, tenantData, 'Failed to load organizations'));
      }

      setTenants(tenantData || []);

      if (inquiryRes.ok) {

        const inquiryData = await parseJsonResponse(inquiryRes);

        setInquiries(inquiryData.inquiries || []);

      }

    } catch (err) {

      setError(err.message || 'Failed to load admin data');

    } finally {

      setLoading(false);

    }

  }, []);



  useEffect(() => {

    loadData();

  }, [loadData]);



  const toggleOrgExpanded = (tenantId) => {

    setExpandedOrgId((current) => (current === tenantId ? null : tenantId));

  };



  const handleCreate = async (event) => {

    event.preventDefault();

    setCreating(true);

    setError(null);

    try {

      const response = await apiFetch('/api/admin/tenants', {

        method: 'POST',

        body: JSON.stringify({

          tenant_id: form.tenant_id.trim(),

          name: form.name.trim(),

          is_platform_admin: false,

          product_entitlement: form.product_entitlement,

        }),

      });

      const data = await parseJsonResponse(response);

      if (!response.ok) {

        throw new Error(apiResponseError(response, data, `Failed to create organization (HTTP ${response.status})`));

      }

      setSuccess(`Created ${data.tenant.name}. Click the row below to add users.`);

      setExpandedOrgId(data.tenant.tenant_id);

      setForm(emptyForm);

      await loadData();

    } catch (err) {

      setError(err.message || 'Failed to create organization');

    } finally {

      setCreating(false);

    }

  };



  const handleUpdateEntitlement = async (tenantRow, productEntitlement, event) => {

    event.stopPropagation();

    setError(null);

    try {

      const response = await apiFetch(`/api/admin/tenants/${tenantRow.tenant_id}`, {

        method: 'PATCH',

        body: JSON.stringify({ product_entitlement: productEntitlement }),

      });

      const data = await parseJsonResponse(response);

      if (!response.ok) {

        throw new Error(apiResponseError(response, data, 'Failed to update product entitlement'));

      }

      await loadData();

    } catch (err) {

      setError(err.message || 'Failed to update product entitlement');

    }

  };



  const handleDeleteOrg = async (tenantRow, event) => {

    event.stopPropagation();

    if (!canDeleteOrg(tenantRow, authProfile?.role)) {

      return;

    }

    const label = tenantRow.name || tenantRow.tenant_id;

    if (!window.confirm(

      `Permanently delete organization "${label}" (${tenantRow.tenant_id})? `

      + 'All users, conversations, and sign-in keys for this org will be removed. This cannot be undone.',

    )) {

      return;

    }

    setDeletingOrgId(tenantRow.tenant_id);

    setError(null);

    setSuccess(null);

    try {

      const response = await apiFetch(`/api/admin/tenants/${tenantRow.tenant_id}`, {

        method: 'DELETE',

      });

      const data = await parseJsonResponse(response);

      if (!response.ok) {

        throw new Error(apiResponseError(response, data, 'Failed to delete organization'));

      }

      if (expandedOrgId === tenantRow.tenant_id) {

        setExpandedOrgId(null);

      }

      setSuccess(

        `Deleted ${label}. ${data.users_removed ?? 0} user(s) removed.`,

      );

      await loadData();

    } catch (err) {

      setError(err.message || 'Failed to delete organization');

    } finally {

      setDeletingOrgId(null);

    }

  };



  const handleToggleStatus = async (tenantRow, event) => {

    event.stopPropagation();

    const nextStatus = tenantRow.status === 'active' ? 'disabled' : 'active';

    const action = nextStatus === 'disabled' ? 'Disable' : 'Enable';

    if (!window.confirm(`${action} sign-in for ${tenantRow.name}?`)) {

      return;

    }

    setError(null);

    try {

      const response = await apiFetch(`/api/admin/tenants/${tenantRow.tenant_id}`, {

        method: 'PATCH',

        body: JSON.stringify({ status: nextStatus }),

      });

      const data = await parseJsonResponse(response);

      if (!response.ok) {

        throw new Error(apiResponseError(response, data, 'Failed to update organization'));

      }

      await loadData();

    } catch (err) {

      setError(err.message || 'Failed to update organization');

    }

  };



  return (

    <div className="h-full min-h-0 bg-black text-white flex flex-col overflow-hidden">

      <header className="shrink-0 bg-black border-b border-teal-500/20">

        <div className="container mx-auto px-6 py-4 flex items-center justify-between gap-4">

          <div>

            <p className="text-teal-400 text-sm font-semibold uppercase tracking-wide">Platform Admin</p>

            <h1 className="text-2xl font-semibold">{SE_BRAND.productName}</h1>

          </div>

          <button

            type="button"

            onClick={onBack}

            className="px-4 py-2 bg-gray-900 hover:bg-gray-800 border border-teal-500/30 rounded-lg text-sm"

          >

            Back to Consultant

          </button>

        </div>

      </header>



      <main className="flex-1 min-h-0 overflow-y-auto overscroll-contain container mx-auto px-6 py-6 max-w-6xl">

        <div className="flex gap-2 mb-6 flex-wrap">

          <button

            type="button"

            onClick={() => changeTab('organizations')}

            className={`px-4 py-2 rounded-lg text-sm font-medium ${activeTab === 'organizations' ? 'bg-teal-500 text-black' : 'bg-gray-900 border border-teal-500/20 text-gray-300'}`}

          >

            Organizations

          </button>

          <button

            type="button"

            onClick={() => changeTab('team')}

            className={`px-4 py-2 rounded-lg text-sm font-medium ${activeTab === 'team' ? 'bg-teal-500 text-black' : 'bg-gray-900 border border-teal-500/20 text-gray-300'}`}

          >

            Platform team

          </button>

          <button

            type="button"

            onClick={() => changeTab('inquiries')}

            className={`px-4 py-2 rounded-lg text-sm font-medium ${activeTab === 'inquiries' ? 'bg-teal-500 text-black' : 'bg-gray-900 border border-teal-500/20 text-gray-300'}`}

          >

            {REQUESTS_TAB_LABEL}

          </button>

        </div>



        {error && (

          <div className="mb-4 text-sm text-red-400 bg-red-950/40 border border-red-500/30 rounded-lg px-3 py-2">

            {error}

          </div>

        )}



        {success && (

          <div className="mb-4 text-sm text-teal-200 bg-teal-950/40 border border-teal-500/30 rounded-lg px-3 py-2">

            {success}

          </div>

        )}



        {activeTab === 'team' && (

          <section className="bg-gray-950 border border-teal-500/20 rounded-xl p-5">

            <h2 className="text-lg font-semibold text-teal-400 mb-4">Platform team</h2>

            <p className="text-sm text-gray-400 mb-4">

              Manage Social Engineer staff accounts for the platform organization (

              <code className="text-white">{authProfile?.tenant_id || PLATFORM_TENANT_ID}</code>

              ). Each user signs in with Organization ID + their personal access key.

            </p>

            <OrgUsersPanel

              canManageUsers

              actorRole={authProfile?.role}

              actorUserId={authProfile?.user_id}

              scopeTenantId={authProfile?.tenant_id}

              variant="admin"

              groupByRole

            />

          </section>

        )}



        {activeTab === 'organizations' && (

          <div className="space-y-6">

            <section className="bg-gray-950 border border-teal-500/20 rounded-xl p-5">

              <h2 className="text-lg font-semibold text-teal-400 mb-4">Add Customer Organization</h2>

              <form onSubmit={handleCreate} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">

                <div>

                  <label className="block text-sm text-gray-300 mb-1">Organization ID</label>

                  <input

                    value={form.tenant_id}

                    onChange={(e) => setForm({ ...form, tenant_id: e.target.value })}

                    required

                    placeholder="acme-corp"

                    className="w-full px-3 py-2 bg-black border border-teal-500/30 rounded-lg"

                  />

                </div>

                <div>

                  <label className="block text-sm text-gray-300 mb-1">Display Name</label>

                  <input

                    value={form.name}

                    onChange={(e) => setForm({ ...form, name: e.target.value })}

                    required

                    placeholder="ACME Corporation"

                    className="w-full px-3 py-2 bg-black border border-teal-500/30 rounded-lg"

                  />

                </div>

                <div>

                  <label className="block text-sm text-gray-300 mb-1">Product entitlement</label>

                  <select

                    value={form.product_entitlement}

                    onChange={(e) => setForm({ ...form, product_entitlement: e.target.value })}

                    className="w-full px-3 py-2 bg-black border border-teal-500/30 rounded-lg"

                  >

                    {PRODUCT_ENTITLEMENT_OPTIONS.map((option) => (

                      <option key={option.value} value={option.value}>{option.label}</option>

                    ))}

                  </select>

                </div>

                <button

                  type="submit"

                  disabled={creating}

                  className="w-full px-4 py-2 bg-teal-500 hover:bg-teal-400 disabled:bg-gray-700 text-black font-semibold rounded-lg"

                >

                  {creating ? 'Creating…' : 'Create Organization'}

                </button>

              </form>

            </section>



            <section className="min-w-0 bg-gray-950 border border-teal-500/20 rounded-xl p-5">

              <div className="flex items-center justify-between mb-4">

                <div>

                  <h2 className="text-lg font-semibold text-teal-400">Organizations</h2>

                  <p className="text-sm text-gray-500 mt-1">Click a row to expand team members below it.</p>

                </div>

                <button type="button" onClick={loadData} className="text-sm text-gray-400 hover:text-white">

                  Refresh

                </button>

              </div>

              {loading ? (

                <p className="text-gray-400 text-sm">Loading…</p>

              ) : tenants.length === 0 ? (

                <p className="text-gray-400 text-sm">No organizations yet.</p>

              ) : (

                <div className="-mx-1 px-1 overflow-x-auto">

                  <table className="w-full min-w-[48rem] text-sm table-auto">

                    <thead>

                      <tr className="text-left text-gray-400 border-b border-gray-800">

                        <th className="py-2 pr-3 w-8" aria-label="Expand" />

                        <th className="py-2 pr-3">Organization</th>

                        <th className="py-2 pr-3">Product</th>

                        <th className="py-2 pr-3">Status</th>

                        <th className="py-2 pr-3">Usage</th>

                        <th className="py-2 pl-2 min-w-[11rem]">Actions</th>

                      </tr>

                    </thead>

                    <tbody>

                      {tenants.map((row) => {

                        const expanded = expandedOrgId === row.tenant_id;

                        const showDelete = canDeleteOrg(row, authProfile?.role);

                        return (

                          <React.Fragment key={row.tenant_id}>

                            <tr

                              className={`border-b border-gray-900 cursor-pointer transition-colors ${

                                expanded ? 'bg-teal-950/20' : 'hover:bg-gray-900/40'

                              }`}

                              onClick={() => toggleOrgExpanded(row.tenant_id)}

                            >

                              <td className="py-3 pr-1 text-gray-500 align-top">

                                <span className="inline-block w-4 text-center" aria-hidden="true">

                                  {expanded ? '▾' : '▸'}

                                </span>

                              </td>

                              <td className="py-3 pr-3">

                                <div className="font-medium text-white">{row.name}</div>

                                <div className="text-xs text-gray-500">{row.tenant_id}</div>

                                {isPlatformOrg(row) && (

                                  <span className="inline-block mt-1 text-xs px-2 py-0.5 rounded bg-teal-900/40 text-teal-300">

                                    Platform org

                                  </span>

                                )}

                              </td>

                              <td className="py-3 pr-3" onClick={(e) => e.stopPropagation()}>

                                <select

                                  value={row.product_entitlement || 'wraith'}

                                  onChange={(e) => handleUpdateEntitlement(row, e.target.value, e)}

                                  className="px-2 py-1 bg-black border border-teal-500/30 rounded text-xs max-w-full"

                                >

                                  {PRODUCT_ENTITLEMENT_OPTIONS.map((option) => (

                                    <option key={option.value} value={option.value}>{option.label}</option>

                                  ))}

                                </select>

                              </td>

                              <td className="py-3 pr-3">

                                <span className={row.status === 'active' ? 'text-green-400' : 'text-red-400'}>

                                  {row.status}

                                </span>

                              </td>

                              <td className="py-3 pr-3 text-gray-400 whitespace-nowrap">

                                {row.conversation_count} chats · {row.inquiry_count} inquiries

                              </td>

                              <td className="py-3 pl-2 align-top" onClick={(e) => e.stopPropagation()}>

                                <div className="flex flex-col gap-1.5 items-start min-w-[9rem]">

                                  <button

                                    type="button"

                                    onClick={(e) => handleToggleStatus(row, e)}

                                    className="px-2 py-1 text-xs whitespace-nowrap bg-gray-900 border border-gray-700 rounded w-full text-left"

                                  >

                                    {row.status === 'active' ? 'Disable' : 'Enable'}

                                  </button>

                                  {showDelete && (

                                    <button

                                      type="button"

                                      onClick={(e) => handleDeleteOrg(row, e)}

                                      disabled={deletingOrgId === row.tenant_id}

                                      className="px-2 py-1 text-xs whitespace-nowrap rounded border border-red-500/50 bg-red-950/40 text-red-300 hover:bg-red-950/70 disabled:opacity-50 w-full text-left"

                                    >

                                      {deletingOrgId === row.tenant_id ? 'Deleting…' : 'Delete org'}

                                    </button>

                                  )}

                                </div>

                              </td>

                            </tr>

                            {expanded && (

                              <tr className="border-b border-gray-900 bg-black/30">

                                <td colSpan={6} className="px-4 py-4" onClick={(e) => e.stopPropagation()}>

                                  <OrgUsersPanel

                                    canManageUsers

                                    actorRole={authProfile?.role}

                                    actorUserId={authProfile?.user_id}

                                    scopeTenantId={row.tenant_id}

                                    variant="admin"

                                    groupByRole

                                    embedded

                                  />

                                </td>

                              </tr>

                            )}

                          </React.Fragment>

                        );

                      })}

                    </tbody>

                  </table>

                </div>

              )}

            </section>

          </div>

        )}



        {activeTab === 'inquiries' && (

          <section className="bg-gray-950 border border-teal-500/20 rounded-xl p-5">

            <div className="flex items-center justify-between mb-4">

              <h2 className="text-lg font-semibold text-teal-400">{REQUESTS_TAB_LABEL}</h2>

              <button type="button" onClick={loadData} className="text-sm text-gray-400 hover:text-white">

                Refresh

              </button>

            </div>

            {loading ? (

              <p className="text-gray-400 text-sm">Loading…</p>

            ) : inquiries.length === 0 ? (

              <p className="text-gray-400 text-sm">No requests yet.</p>

            ) : (

              <div className="space-y-3">

                {inquiries.map((item, index) => (

                  <div key={`${item.tenant_id}-${item.session_id || item.subject}-${index}`} className="border border-gray-800 rounded-lg p-4">

                    <div className="flex flex-wrap items-center justify-between gap-2 mb-2">

                      <div className="text-sm">

                        <span className="text-teal-300">{item.email || item.user_name || 'Unknown sender'}</span>

                        <span className="text-gray-500 ml-2">· {item.tenant_id}</span>

                      </div>

                      <div className="text-xs text-gray-500">

                        {item.submitted_at ? new Date(item.submitted_at).toLocaleString() : ''}

                      </div>

                    </div>

                    {item.subject && (

                      <p className="text-sm font-medium text-white">{item.subject}</p>

                    )}

                    <p className="text-sm text-gray-300">

                      {requestTypeLabel(item)}

                    </p>

                    {item.message && (

                      <p className="text-sm text-gray-300 mt-2 whitespace-pre-wrap">{item.message}</p>

                    )}

                    {item.conversation_summary && (

                      <p className="text-xs text-gray-500 mt-2 line-clamp-3">{item.conversation_summary}</p>

                    )}

                  </div>

                ))}

              </div>

            )}

          </section>

        )}

      </main>

    </div>

  );

}

